import * as Sentry from "@sentry/react";

const PII_KEY_RE = /email|phone|address|token|password|secret|card|cvv|ssn/i;
const SENSITIVE_HEADERS = new Set([
  "authorization",
  "cookie",
  "x-api-key",
  "x-auth-token",
]);
const IGNORED_EXCEPTION_TYPES = new Set([
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
  "Non-Error promise rejection captured",
]);

export function beforeSend(
  event: Sentry.ErrorEvent,
  _hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  for (const exc of event.exception?.values ?? []) {
    if (exc.type && IGNORED_EXCEPTION_TYPES.has(exc.type)) return null;
  }

  const headers = (event.request?.headers ?? {}) as Record<string, string>;
  for (const name of Object.keys(headers)) {
    if (SENSITIVE_HEADERS.has(name.toLowerCase())) {
      headers[name] = "[Filtered]";
    }
  }

  const extra = (event.extra ?? {}) as Record<string, unknown>;
  for (const key of Object.keys(extra)) {
    if (PII_KEY_RE.test(key)) extra[key] = "[Filtered]";
  }

  return event;
}

export function initSentry(): void {
  if (import.meta.env.DEV) {
    // Development uses the browser console and the local monitoring agent.
    // Keep this hard gate before the DSN read so a populated .env cannot
    // accidentally send local errors or traces to Sentry.
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn("Sentry disabled: VITE_SENTRY_DSN not set");
    return;
  }
  try {
    Sentry.init({
      dsn,
      environment:
        (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
        "development",
      release:
        (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ??
        "unknown",
      tracesSampleRate: Number(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "1.0"
      ),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendDefaultPii: false,
      beforeSend,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Sentry init failed; continuing without observability", e);
  }
}
